import { c as _c } from "react/compiler-runtime";
import { add, differenceInSeconds } from 'date-fns';
import { Form, Formik } from 'formik';
import { useEffect, useRef } from 'react';
import { Resolution } from '../../../../utils/chart';
import { RelativeDate } from '../../../../utils/date';
import ErrorBoundary from '../../../common/helpers/ErrorBoundary';
import HistogramBarChart, { DimensionConfig, HistogramBarData } from '../../../common/Histogram/HistogramBarChart';
import HistogramRangePicker from '../../../common/Histogram/HistogramRangePicker';
import { useOutpostMetricsQuery, OutpostMetricsQueryParams } from '../../../hooks/useOutpostMetricsQuery';
import { getHistogramConfigs } from '../../DashboardScene/Events/Histogram';
interface OutpostHistogramFilters {
  date?: {
    relative?: RelativeDate;
    min?: string;
    max?: string;
  };
}
const ATTEMPTS_STATUS_DIMENSION_CONFIGS: DimensionConfig[] = [{
  value: 'success',
  color: 'green_histogram',
  label: 'Succeeded'
}, {
  value: 'failed',
  color: 'red_histogram',
  label: 'Failed'
}];
const OutpostHistogramChart: React.FC<{
  start_date: Date;
  end_date: Date;
  resolution: Resolution;
  refresh_key?: string;
  endpoint?: 'events' | 'attempts';
  label?: string;
  metrics_filters?: Record<string, string | string[]>;
  dimension?: string;
  dimension_configs?: DimensionConfig[];
}> = t0 => {
  const $ = _c(37);
  const {
    start_date,
    end_date,
    resolution,
    refresh_key,
    endpoint: t1,
    label: t2,
    metrics_filters,
    dimension,
    dimension_configs
  } = t0;
  const endpoint = t1 === undefined ? "events" : t1;
  const label = t2 === undefined ? "Events" : t2;
  const first_render_refresh_key = useRef(refresh_key);
  let t3;
  if ($[0] !== start_date) {
    t3 = start_date.toISOString();
    $[0] = start_date;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== end_date) {
    t4 = end_date.toISOString();
    $[2] = end_date;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== t3 || $[5] !== t4) {
    t5 = {
      start: t3,
      end: t4
    };
    $[4] = t3;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const t6 = `1${resolution.granularity[0]}`;
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = ["count"];
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== metrics_filters) {
    t8 = metrics_filters || {};
    $[8] = metrics_filters;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== dimension) {
    t9 = dimension ? {
      dimensions: [dimension]
    } : {};
    $[10] = dimension;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== endpoint || $[13] !== t5 || $[14] !== t6 || $[15] !== t8 || $[16] !== t9) {
    t10 = {
      endpoint,
      time: t5,
      granularity: t6,
      measures: t7,
      filters: t8,
      ...t9
    };
    $[12] = endpoint;
    $[13] = t5;
    $[14] = t6;
    $[15] = t8;
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  const metrics_query = t10;
  const {
    raw_data,
    refetch,
    is_loading
  } = useOutpostMetricsQuery(metrics_query);
  let t11;
  if ($[18] !== is_loading || $[19] !== refetch || $[20] !== refresh_key) {
    t11 = () => {
      if (refresh_key && refetch && !is_loading && first_render_refresh_key.current !== refresh_key) {
        refetch();
      }
    };
    $[18] = is_loading;
    $[19] = refetch;
    $[20] = refresh_key;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== refresh_key) {
    t12 = [refresh_key];
    $[22] = refresh_key;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  useEffect(t11, t12);
  let t13;
  if ($[24] !== dimension || $[25] !== endpoint || $[26] !== raw_data || $[27] !== resolution.factor_to_seconds || $[28] !== resolution.steps || $[29] !== resolution.unit || $[30] !== start_date) {
    bb0: {
      const model_prefix = endpoint === "attempts" ? "Attempts" : "Events";
      const dimension_key = dimension ? `${model_prefix}.${dimension}` : null;
      const series = raw_data?.map(row => ({
        date: row[`${model_prefix}.createdAt`],
        count: parseInt(row[`${model_prefix}.count`] || "0", 10),
        dimension_value: dimension_key ? row[dimension_key] : null
      }));
      if (!series) {
        t13 = null;
        break bb0;
      }
      t13 = Array.from(Array(resolution.steps).keys()).map(index => {
        const step_start = add(start_date, {
          seconds: index * resolution.unit * resolution.factor_to_seconds
        });
        const step_end = add(step_start, {
          seconds: resolution.unit * resolution.factor_to_seconds
        });
        const values = {};
        let total = 0;
        series.forEach(row_0 => {
          const row_time = new Date(row_0.date).getTime();
          if (row_time < step_end.getTime() && row_time >= step_start.getTime()) {
            if (row_0.dimension_value) {
              values[row_0.dimension_value] = (values[row_0.dimension_value] || 0) + row_0.count;
            }
            total = total + row_0.count;
            total;
          }
        });
        return {
          start: step_start,
          end: step_end,
          values,
          total
        };
      });
    }
    $[24] = dimension;
    $[25] = endpoint;
    $[26] = raw_data;
    $[27] = resolution.factor_to_seconds;
    $[28] = resolution.steps;
    $[29] = resolution.unit;
    $[30] = start_date;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  const bars = t13;
  let t14;
  if ($[32] !== bars || $[33] !== dimension_configs || $[34] !== label || $[35] !== resolution) {
    t14 = <HistogramBarChart bars={bars} resolution={resolution} dimension_configs={dimension_configs} tooltip_label={label} />;
    $[32] = bars;
    $[33] = dimension_configs;
    $[34] = label;
    $[35] = resolution;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  return t14;
};
const Histogram: React.FC<{
  filters: OutpostHistogramFilters;
  start_date: Date;
  end_date: Date;
  setDate: (date: {
    relative?: RelativeDate;
    min?: string;
    max?: string;
  } | undefined) => void;
  refresh_key?: string;
  endpoint?: 'events' | 'attempts';
  label?: string;
  metrics_filters?: Record<string, string | string[]>;
  dimension?: string;
  dimension_configs?: DimensionConfig[];
}> = t0 => {
  const $ = _c(41);
  const {
    start_date,
    end_date,
    setDate,
    refresh_key,
    endpoint,
    label,
    metrics_filters,
    dimension,
    dimension_configs
  } = t0;
  let T0;
  let T1;
  let axis_resolution;
  let chart_resolution;
  let resolved_end_date;
  let resolved_start_date;
  let t1;
  let t2;
  if ($[0] !== end_date || $[1] !== start_date) {
    const diff = differenceInSeconds(end_date, start_date);
    ({
      start_date: resolved_start_date,
      end_date: resolved_end_date,
      chart_resolution,
      axis_resolution
    } = getHistogramConfigs(diff, start_date, end_date));
    T1 = ErrorBoundary;
    T0 = Formik;
    t1 = resolved_start_date.toISOString();
    t2 = resolved_end_date.toISOString();
    $[0] = end_date;
    $[1] = start_date;
    $[2] = T0;
    $[3] = T1;
    $[4] = axis_resolution;
    $[5] = chart_resolution;
    $[6] = resolved_end_date;
    $[7] = resolved_start_date;
    $[8] = t1;
    $[9] = t2;
  } else {
    T0 = $[2];
    T1 = $[3];
    axis_resolution = $[4];
    chart_resolution = $[5];
    resolved_end_date = $[6];
    resolved_start_date = $[7];
    t1 = $[8];
    t2 = $[9];
  }
  const t3 = t1 + t2;
  const t4 = chart_resolution.steps - 1;
  let t5;
  if ($[10] !== t4) {
    t5 = {
      timebar_start: 0,
      timebar_end: t4
    };
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== chart_resolution.factor_to_seconds || $[13] !== chart_resolution.unit || $[14] !== resolved_start_date || $[15] !== setDate) {
    t6 = values => {
      setDate({
        relative: undefined,
        min: add(resolved_start_date, {
          seconds: values.timebar_start * chart_resolution.unit * chart_resolution.factor_to_seconds
        }).toISOString(),
        max: add(resolved_start_date, {
          seconds: (values.timebar_end + 1) * chart_resolution.unit * chart_resolution.factor_to_seconds
        }).toISOString()
      });
    };
    $[12] = chart_resolution.factor_to_seconds;
    $[13] = chart_resolution.unit;
    $[14] = resolved_start_date;
    $[15] = setDate;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== chart_resolution || $[18] !== dimension || $[19] !== dimension_configs || $[20] !== endpoint || $[21] !== label || $[22] !== metrics_filters || $[23] !== refresh_key || $[24] !== resolved_end_date || $[25] !== resolved_start_date) {
    t7 = <OutpostHistogramChart start_date={resolved_start_date} end_date={resolved_end_date} resolution={chart_resolution} refresh_key={refresh_key} endpoint={endpoint} label={label} metrics_filters={metrics_filters} dimension={dimension} dimension_configs={dimension_configs} />;
    $[17] = chart_resolution;
    $[18] = dimension;
    $[19] = dimension_configs;
    $[20] = endpoint;
    $[21] = label;
    $[22] = metrics_filters;
    $[23] = refresh_key;
    $[24] = resolved_end_date;
    $[25] = resolved_start_date;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  let t8;
  if ($[27] !== axis_resolution || $[28] !== chart_resolution || $[29] !== resolved_start_date || $[30] !== t7) {
    t8 = <Form><HistogramRangePicker chart_resolution={chart_resolution} axis_resolution={axis_resolution} start_date={resolved_start_date}>{t7}</HistogramRangePicker></Form>;
    $[27] = axis_resolution;
    $[28] = chart_resolution;
    $[29] = resolved_start_date;
    $[30] = t7;
    $[31] = t8;
  } else {
    t8 = $[31];
  }
  let t9;
  if ($[32] !== T0 || $[33] !== t3 || $[34] !== t5 || $[35] !== t6 || $[36] !== t8) {
    t9 = <T0 key={t3} initialValues={t5} onSubmit={t6}>{t8}</T0>;
    $[32] = T0;
    $[33] = t3;
    $[34] = t5;
    $[35] = t6;
    $[36] = t8;
    $[37] = t9;
  } else {
    t9 = $[37];
  }
  let t10;
  if ($[38] !== T1 || $[39] !== t9) {
    t10 = <T1>{t9}</T1>;
    $[38] = T1;
    $[39] = t9;
    $[40] = t10;
  } else {
    t10 = $[40];
  }
  return t10;
};
export { ATTEMPTS_STATUS_DIMENSION_CONFIGS };
export default Histogram;